import { useContext, useMemo } from 'react';
import { AuthContext } from 'contexts/AuthContext';

type TokenClaims = Record<string, unknown>;

/**
 * Decodes the Cognito ID token (JWT) payload to read its claims. No signature
 * verification — display use only, never for trust decisions.
 */
function decodeIdToken(idToken?: string | null): TokenClaims | null {
  if (!idToken) return null;
  const parts = idToken.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Returns the current user's identity derived from the Cognito ID token claims.
 * The Okta login lands under `identities[].userId` (federated SAML identity);
 * we fall back to standard claims just in case. Read-only / display use only.
 */
export interface CurrentUser {
  email?: string;
  provider?: string;
  claims: TokenClaims;
}

export function useCurrentUser(): CurrentUser | null {
  // Read the context directly (not useAuthContext, which throws) so this
  // display-only hook degrades to null when no provider is present.
  const idToken = useContext(AuthContext)?.idToken;
  return useMemo(() => {
    const claims = decodeIdToken(idToken);
    if (!claims) return null;

    const identities = (claims.identities as Array<Record<string, unknown>> | undefined) ?? [];
    const primary = identities[0];

    const email =
      (primary?.userId as string | undefined) ??
      (claims.email as string | undefined) ??
      (claims['cognito:username'] as string | undefined);

    return {
      email,
      provider: primary?.providerName as string | undefined,
      claims,
    };
  }, [idToken]);
}
